import { lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { PageLayout } from 'app/domains/common-ui';
import { AuthRoutes, PublicRoute, SuspenseRoute } from 'app/domains/urls';
import { PageLoader } from 'shared/domains/common-ui';
import { workspaceToDashboardRedirect, workspaceToFilesRedirect } from './App/screens/Dashboard/utils';
const SignedOutPage = lazy(() => import(/* webpackChunkName: "signed-out" */ './SignedOut'));
const NotFound = lazy(() => import(/* webpackChunkName: "not-found" */ './App/screens/NotFound'));
const AccountRoute = lazy(() => import(/* webpackChunkName: "settings" */ './App/screens/Settings'));
const LogoutPage = lazy(() => import(/* webpackChunkName: "logout" */ './Logout'));
const SignupPage = lazy(() => import(/* webpackChunkName: "signup" */ './Signup'));
const CompanyCreationPage = lazy(() => import(/* webpackChunkName: "company-creation" */ './CompanyCreation'));
const InvitePage = lazy(() => import(/* webpackChunkName: "invite" */ './Invite'));
const VerifyPage = lazy(() => import(/* webpackChunkName: "verify" */ './Verify'));
const DraftPage = lazy(() => import(/* webpackChunkName: "draft" */ './App/screens/Draft'));
const DraftEditorPage = lazy(() => import(/* webpackChunkName: "draft-editor" */ './App/screens/DraftEditor'));
const ContractsPage = lazy(() => import(/* webpackChunkName: "contracts" */ './App/screens/Contracts'));
const DashboardPage = lazy(() => import(/* webpackChunkName: "dashboard" */ './App/screens/Dashboard'));
const ProjectsPage = lazy(() => import(/* webpackChunkName: "projects" */ './App/screens/Projects'));
const InsightsPage = lazy(() => import(/* webpackChunkName: "insights" */ './App/screens/Insights/screens/DashboardScreen'));
const EmptyInsightsPage = lazy(() => import(/* webpackChunkName: "insights-empty" */ './App/screens/Insights/screens/EmptyDashboardScreen'));
const FilesSystemPage = lazy(() => import(/* webpackChunkName: "file-system" */ './App/screens/FileSystem'));
const DocumentPage = lazy(() => import(/* webpackChunkName: "document" */ './App/screens/Document'));
const CheckoutPage = lazy(() => import(/* webpackChunkName: "checkout" */ './App/screens/Checkout'));
const SearchPage = lazy(() => import(/* webpackChunkName: "search" */ './App/screens/Search'));
const UsersTeamsPage = lazy(() => import(/* webpackChunkName: "users-teams" */ './App/screens/UsersTeams'));
const TasksPage = lazy(() => import(/* webpackChunkName: "tasks" */ './App/screens/Tasks'));
const AllDocumentsPage = lazy(() => import(/* webpackChunkName: "all-documents" */ './App/screens/AllDocuments'));
const SavedViewPage = lazy(() => import(/* webpackChunkName: "saved-view" */ './App/screens/SavedView'));
const NoticeDatesPage = lazy(() => import(/* webpackChunkName: "notice-dates" */ './App/screens/NoticeDates'));
const DraftsPage = lazy(() => import(/* webpackChunkName: "drafts" */ './App/screens/Drafts'));
const GlobalPlaiPage = lazy(() => import(/* webpackChunkName: "global-plai" */ './App/screens/GlobalPlai'));
const IntegrationsRoute = lazy(() => import(/* webpackChunkName: "integrations" */ './App/screens/Integrations'));
const AuthSSO = lazy(() => import(/* webpackChunkName: "auth-sso" */ 'app/domains/auth/sso/AuthSSO'));
const CreateTemplate = lazy(() => import(/* webpackChunkName: "template-new-screen" */ 'app/domains/templates/screens/CreateTemplate'));
const EditTemplate = lazy(() => import(/* webpackChunkName: "template-editing-screen" */ 'app/domains/templates/screens/EditTemplate'));
const WorkflowsPage = lazy(() => import(/* webpackChunkName: "workflows" */ 'app/domains/workflows/screens/Entry'));
const BackOffice = lazy(() => import(/* webpackChunkName: "back-office" */ './App/screens/CustomerSuccess/BackOffice'));
const BackOfficeCompanySelection = lazy(() => import(/* webpackChunkName: "back-office-company-selection" */ './App/screens/CustomerSuccess/CompanySelection'));
export function Routes() {
    return (<Suspense fallback={<PageLoader />}>
      <Switch>
        <Redirect from="/sv-se/*" to="/*"/>
        <Redirect exact from="/" to="/dashboard"/>
        <Redirect exact path="/login" to="/"/>

        {/* make sure to edit `isPublicRoute` function if you add/remove public routes */}
        <PublicRoute exact path="/logout" component={LogoutPage}/>
        <PublicRoute exact path="/signup" component={SignupPage}/>
        <PublicRoute exact path="/signed-out" component={SignedOutPage}/>
        <PublicRoute exact path="/invite/:id" component={InvitePage}/>
        <PublicRoute exact path="/company-creation" component={CompanyCreationPage}/>
        <PublicRoute exact path="/auth/sso" component={AuthSSO}/>

        <AuthRoutes>
          <SuspenseRoute path="/verify" component={VerifyPage}/>
          <SuspenseRoute path="/checkout" component={CheckoutPage}/>

          <SuspenseRoute exact path="/draft/:id" component={DraftPage}/>
          <SuspenseRoute exact path="/draft/:id/chapter/:ref" component={DraftEditorPage}/>

          <PageLayout>
            <Switch>
              <SuspenseRoute exact path="/contracts" component={ContractsPage}/>

              <Route exact path="/workspaces" render={(routeProps) => <Redirect to={workspaceToDashboardRedirect(routeProps)}/>}/>

              <SuspenseRoute exact path="/dashboard" component={DashboardPage}/>
              <SuspenseRoute path="/projects" component={ProjectsPage}/>

              <SuspenseRoute exact path="/insights" component={EmptyInsightsPage}/>
              <SuspenseRoute path="/insights/:id" component={InsightsPage}/>

              <SuspenseRoute path="/plai" component={GlobalPlaiPage}/>

              <Route path={['/workspaces/:workspaceId/folders/:folderId', '/workspaces/:workspaceId']} render={(routeProps) => <Redirect to={workspaceToFilesRedirect(routeProps)}/>}/>

              <SuspenseRoute path="/document/:id" component={DocumentPage}/>

              <SuspenseRoute path="/files/:folderId?" component={FilesSystemPage}/>

              <SuspenseRoute path="/templates/create" component={CreateTemplate}/>
              <SuspenseRoute path="/templates/:templateId/edit" component={EditTemplate}/>

              <SuspenseRoute path="/search" component={SearchPage}/>
              <SuspenseRoute path="/all-documents" component={AllDocumentsPage}/>
              <SuspenseRoute path="/saved-view/:viewId" component={SavedViewPage}/>

              <SuspenseRoute path="/drafts" component={DraftsPage}/>
              <SuspenseRoute exact path="/documents/notice-dates" component={NoticeDatesPage}/>

              <Redirect exact from="/settings/*" to="/account"/>
              <SuspenseRoute path="/account" component={AccountRoute}/>

              <SuspenseRoute path="/integrations" component={IntegrationsRoute}/>

              <SuspenseRoute path="/users-teams" component={UsersTeamsPage}/>

              <SuspenseRoute path="/rules" component={WorkflowsPage}/>

              <SuspenseRoute exact path="/tasks" component={TasksPage}/>

              <SuspenseRoute exact path="/back-office" component={BackOfficeCompanySelection}/>
              <SuspenseRoute path="/back-office/:companyId" component={BackOffice}/>

              <SuspenseRoute>
                <NotFound />
              </SuspenseRoute>
            </Switch>
          </PageLayout>
        </AuthRoutes>
      </Switch>
    </Suspense>);
}
